import {
  Grid,
  GridItem,
  Icon,
  Menu,
  MenuList,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import React from "react";
import { HiOutlineBriefcase } from "react-icons/hi";
import { MdMoreVert } from "react-icons/md";

import { IconButton, Link, MenuButton } from "../../../../components";
import { AiChatIcon, DateIcon } from "../../../../components/Icons";
import { useIsSmallScreen } from "../../../../hooks/useIsSmallScreen";
import { formatDate } from "../../../../utils/datetime";
import MobileDetailsHeader, {
  MobileDetailsHeaderProps,
} from "./MobileDetailsHeader";

export type DetailsHeaderProps = MobileDetailsHeaderProps & {
  share?: JSX.Element;
  menuOptions?: JSX.Element[];
  actionButtons?: JSX.Element;
};

const DetailsHeader: React.FC<DetailsHeaderProps> = ({
  candidate,
  position,
  candidateLinks,
  aiInterviewer,
  date,
  share,
  menuOptions = [],
  actionButtons,
}) => {
  const isSmallScreen = useIsSmallScreen();

  if (isSmallScreen) {
    return (
      <MobileDetailsHeader
        candidate={candidate}
        position={position}
        aiInterviewer={aiInterviewer}
        candidateLinks={candidateLinks}
        date={date}
        actionButtons={actionButtons}
      />
    );
  }

  return (
    <Grid
      py="3"
      px="6"
      templateColumns="1fr min-content"
      templateAreas='
        "title    menu"
        "subtitle subtitle"'
    >
      <GridItem gridArea="title" display="flex" alignItems="center">
        {/* Candidate name */}
        <Text
          as="span"
          fontWeight="bold"
          fontSize="2xl"
          mr="4"
          data-tour-id="ip2-candidate-name"
        >
          {candidate?.id ? (
            <Tooltip label="View Candidate" openDelay={300}>
              <Link
                variant="darkGrayBlueHover"
                href={`/candidate/${candidate.id}`}
              >
                {candidate.fullName || "Candidate"}
              </Link>
            </Tooltip>
          ) : (
            <Text display="inline" color="gray.400">
              Unknown Candidate
            </Text>
          )}
        </Text>

        {candidateLinks}
      </GridItem>

      <GridItem
        gridArea="subtitle"
        mt="2"
        display="flex"
        alignItems="center"
        fontWeight="medium"
        whiteSpace="nowrap"
      >
        {position?.id ? (
          <Link
            mr="6"
            minW="0"
            display="flex"
            alignItems="center"
            variant="darkGrayBlueHover"
            href={`/position/${position.id}`}
          >
            <Icon as={HiOutlineBriefcase} mr="1" />
            <Text
              as="span"
              fontSize="sm"
              fontWeight="500"
              textOverflow="ellipsis"
              overflow="hidden"
            >
              {position.displayTitle}
            </Text>
          </Link>
        ) : (
          <Text mr="4" display="flex" alignItems="center" color="gray.400">
            <Icon as={HiOutlineBriefcase} mr="1" />
            No Position
          </Text>
        )}

        <Link
          mr="6"
          minW="0"
          display="flex"
          alignItems="center"
          variant="darkGrayBlueHover"
          href={`/ai-interviewer/${aiInterviewer.id}`}
        >
          <AiChatIcon boxSize="4" mr="1" />
          <Text
            as="span"
            fontSize="sm"
            fontWeight="500"
            whiteSpace="nowrap"
            textOverflow="ellipsis"
            overflow="hidden"
          >
            {aiInterviewer.name}
          </Text>
        </Link>

        <Text fontSize="sm" mx="4" color="gray.600" fontWeight="400">
          <DateIcon boxSize="4" mr="1" mt="-3px" />
          {formatDate(date, {
            year: "numeric",
            month: "short",
            day: "numeric",
          })}
        </Text>

        {actionButtons}
      </GridItem>

      <GridItem gridArea="menu" display="flex" alignItems="center">
        {share}

        {menuOptions.length > 0 && (
          <Menu placement="bottom-start">
            <MenuButton
              as={IconButton}
              icon={<MdMoreVert size="20" />}
              data-testid="call-settings-menu"
              variant="white"
              minW="8"
              h="8"
              data-tour-id="ip2-menu"
              color="gray.900"
            />
            <MenuList>
              {menuOptions.map((option, idx) => (
                // eslint-disable-next-line react/no-array-index-key
                <React.Fragment key={idx}>{option}</React.Fragment>
              ))}
            </MenuList>
          </Menu>
        )}
      </GridItem>
    </Grid>
  );
};

export default DetailsHeader;
